import { useEffect, useRef, useState } from "react";
import Sidebar from "@/components/Sidebar";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Plus, X, Save } from "lucide-react";

export default function BrandKit() {
  const [kit, setKit] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { api.get("/brandkit").then((r) => setKit(r.data)).catch(() => toast.error("Failed to load brand kit")); }, []);

  const update = (patch) => setKit((k) => ({ ...k, ...patch }));

  const onLogo = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => update({ logo: reader.result });
    reader.readAsDataURL(f);
  };

  const save = async () => {
    setSaving(true);
    try { await api.put("/brandkit", kit); toast.success("Brand kit saved"); }
    catch (e) { toast.error("Could not save"); }
    finally { setSaving(false); }
  };

  if (!kit) return (
    <div className="flex bg-kann-base min-h-screen"><Sidebar />
      <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 text-kann-gold animate-spin" /></div>
    </div>
  );

  return (
    <div className="flex bg-kann-base min-h-screen">
      <Sidebar />
      <main className="flex-1 min-w-0 p-4 md:p-10 pt-16 md:pt-10 max-w-4xl relative">
        <div className="absolute inset-0 -z-10 bg-dots overflow-hidden"><div className="glow-orb" style={{ width: 560, height: 560, top: -160, left: "60%" }} /></div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase text-kann-gold font-mono">Consistency</div>
            <h1 className="text-3xl sm:text-4xl font-medium text-kann-text-pri mt-2">Brand Kit</h1>
            <p className="font-light text-sm md:text-base text-kann-text-sec mt-2">Your logo, colors and fonts — reused everywhere you design.</p>
          </div>
          <button onClick={save} disabled={saving} data-testid="save-brandkit-btn"
            className="shrink-0 bg-kann-ink text-white font-semibold rounded-full px-4 sm:px-5 py-2.5 text-sm hover:bg-black transition-colors flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>

        <div className="mt-8 space-y-5">
          <section className="bg-kann-surface border border-kann-border/30 rounded-lg p-6">
            <div className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono mb-4">Identity</div>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="text-sm text-kann-text-pri">Brand name</label>
                <input data-testid="brand-name-input" value={kit.name} onChange={(e) => update({ name: e.target.value })}
                  className="mt-2 w-full bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold focus:ring-1 focus:ring-kann-gold px-3 py-2 text-sm outline-none" />
              </div>
              <div>
                <label className="text-sm text-kann-text-pri">Tagline</label>
                <input data-testid="brand-tagline-input" value={kit.tagline} onChange={(e) => update({ tagline: e.target.value })}
                  className="mt-2 w-full bg-kann-base border border-kann-border/40 rounded-md text-kann-text-pri focus:border-kann-gold focus:ring-1 focus:ring-kann-gold px-3 py-2 text-sm outline-none" />
              </div>
            </div>
            <div className="mt-5">
              <label className="text-sm text-kann-text-pri">Logo</label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-40 h-24 rounded-md bg-kann-base border border-kann-border/40 flex items-center justify-center overflow-hidden">
                  {kit.logo ? <img src={kit.logo} alt="logo" className="max-w-full max-h-full object-contain" /> : <span className="logo-font text-2xl text-kann-text-pri">KANN<span className="text-kann-gold">.</span></span>}
                </div>
                <button onClick={() => fileRef.current?.click()} data-testid="upload-logo-btn"
                  className="border border-kann-border/50 text-kann-text-pri rounded-md px-4 py-2 text-sm hover:bg-kann-surface-active transition-colors flex items-center gap-2">
                  <Upload className="w-4 h-4" /> Upload logo
                </button>
                {kit.logo && <button onClick={() => update({ logo: null })} className="text-sm text-kann-text-sec hover:text-red-400">Remove</button>}
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onLogo} />
              </div>
            </div>
          </section>

          <section className="bg-kann-surface border border-kann-border/30 rounded-lg p-6">
            <div className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono mb-4">Color palette</div>
            <div className="flex flex-wrap gap-4" data-testid="color-palette">
              {kit.colors.map((c, i) => (
                <div key={i} className="relative group">
                  <label className="block w-20 h-20 rounded-lg border border-kann-border/40 cursor-pointer overflow-hidden" style={{ background: c }}>
                    <input type="color" value={c} onChange={(e) => { const n = [...kit.colors]; n[i] = e.target.value.toUpperCase(); update({ colors: n }); }} className="opacity-0 w-full h-full cursor-pointer" />
                  </label>
                  <div className="text-xs text-kann-text-sec mt-1 font-mono text-center">{c}</div>
                  <button onClick={() => update({ colors: kit.colors.filter((_, x) => x !== i) })}
                    className="absolute -top-2 -right-2 bg-kann-base border border-kann-border/50 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="w-3 h-3 text-kann-text-sec" />
                  </button>
                </div>
              ))}
              <button onClick={() => update({ colors: [...kit.colors, "#DB960D"] })} data-testid="add-color-btn"
                className="w-20 h-20 rounded-lg border border-dashed border-kann-border/50 flex items-center justify-center text-kann-text-sec hover:border-kann-gold hover:text-kann-gold transition-colors">
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </section>

          <section className="bg-kann-surface border border-kann-border/30 rounded-lg p-6">
            <div className="text-xs tracking-[0.2em] uppercase text-kann-text-sec font-mono mb-4">Fonts</div>
            <div className="flex flex-wrap gap-3">
              {kit.fonts.map((f, i) => (
                <span key={i} className="px-4 py-2 rounded-md bg-kann-base border border-kann-border/40 text-kann-text-pri text-sm" style={{ fontFamily: f }}>{f}</span>
              ))}
            </div>
            <p className="text-xs text-kann-text-sec mt-3">Logo uses the <span className="logo-font text-kann-text-pri">KANN<span className="text-kann-gold">.</span></span> wordmark · Body uses Poppins.</p>
          </section>
        </div>
      </main>
    </div>
  );
}
